import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentWatch } from "../../app/appSlice";
import { useLocation } from "react-router-dom";

const ProgressBar = ({ playerRef, isPlayerReady, isVideoEndState }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    isProgressBarFocused,
    isEpisodesBarFocused,
    data,
    selectedEpisode,
    isPlaying,
    isSeeking,
    showResumePrompt,
  } = useSelector((state) => state.app.currentWatch);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [seekPreviewTime, setSeekPreviewTime] = useState(null);
  const seekTimeoutRef = React.useRef(null);
  const seekPreviewTimeRef = React.useRef(null);

  // Update video time from player every 250ms
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !isPlayerReady || !location.pathname.includes("/watch")) {
      setSeekPreviewTime(null);
      clearTimeout(seekTimeoutRef.current);
      dispatch(setCurrentWatch({ isSeeking: false }));
      return;
    }

    let animationFrameId;
  
    const updateTime = () => {
      const current = isSeeking ? seekPreviewTime : player.getCurrentTime();
      const total = player.getDuration();
      if (total) {
        setCurrentTime(current);        
        setDuration(total);
      }
      animationFrameId = requestAnimationFrame(updateTime);
    };
  
    animationFrameId = requestAnimationFrame(updateTime);
  
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [
    dispatch,
    playerRef,
    isPlayerReady,
    seekPreviewTime,
    isSeeking,
    location.pathname, // track path changes here too
  ]);
  
  // Handle Arrow Left/Right to preview seek time, Enter to confirm
  // Keep the ref updated with the current value
  useEffect(() => {
    if(seekPreviewTime >= 0) {
      seekPreviewTimeRef.current = seekPreviewTime;
      dispatch(setCurrentWatch({ seekTime: formatTime(seekPreviewTime) }));
    }
  }, [dispatch, seekPreviewTime]);

  useEffect(() => {
    const holdCountRef = { current: 0 };

    const handleKeyDown = (e) => {
      if (!isProgressBarFocused || isEpisodesBarFocused || showResumePrompt)
        return;
      if (!playerRef.current || !isPlayerReady) return;
      if (
        (e.altKey && (e.key === "ArrowLeft" || e.keyCode === 37)) ||
        (e.altKey && (e.key === "ArrowRight" || e.keyCode === 39))
      )
        return;
      if ((e.key === "Enter" || e.keyCode === 13) && seekPreviewTimeRef.current === null) {
        clearTimeout(seekTimeoutRef.current);
        return;
      }

      const player = playerRef.current;
      const rawCurrent =
        seekPreviewTimeRef.current !== null
          ? seekPreviewTimeRef.current
          : player.getCurrentTime();

      const current = Math.floor(rawCurrent);

      if (seekTimeoutRef.current) {
        clearTimeout(seekTimeoutRef.current);
      }

      seekTimeoutRef.current = setTimeout(() => {
        setSeekPreviewTime(null);
        dispatch(setCurrentWatch({ isSeeking: false }));
        if (isPlaying) player.playVideo();
        holdCountRef.current = 0;
      }, 3000);

      // const step = baseStep * holdCountRef.current;
      const step = e.repeat ? 30 : 10;

      if (e.key === "ArrowRight" || e.keyCode === 39) {
        player.pauseVideo();
        dispatch(setCurrentWatch({ isSeeking: true, isOverlay: true }));
        const next = Math.min(current + step, duration);
        setSeekPreviewTime(next);
      } else if (e.key === "ArrowLeft" || e.keyCode === 37) {
        player.pauseVideo();
        dispatch(setCurrentWatch({ isSeeking: true, isOverlay: true }));
        const prev = Math.max(current - step, 0);
        setSeekPreviewTime(prev);
      } else if ((e.key === "Enter" || e.keyCode === 13) && seekPreviewTimeRef.current !== null) {
        player.seekTo(seekPreviewTimeRef.current, true);
        player.playVideo();
        setTimeout(() => {
          setSeekPreviewTime(null);
          dispatch(setCurrentWatch({ isSeeking: false }));
          holdCountRef.current = 0;
        }, 300);
      } else if (
        (e.key === "ArrowDown" || e.keyCode === 40) &&
        seekPreviewTimeRef.current !== null
      ) {
        setSeekPreviewTime(null);
        dispatch(
          setCurrentWatch({
            isSeeking: false,
            isEpisodesBarFocused: true,
            isProgressBarFocused: false,
          })
        );
        holdCountRef.current = 0;
        player.playVideo();
      } else if (e.key === "Escape") {
        setSeekPreviewTime(null);
        dispatch(setCurrentWatch({ isSeeking: false }));
        holdCountRef.current = 0;
      }
    };

    if (location.pathname.includes("/watch") && isPlayerReady) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      clearTimeout(seekTimeoutRef.current);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    dispatch,
    isProgressBarFocused,
    isEpisodesBarFocused,
    playerRef,
    isPlayerReady,
    duration,
    location.pathname,
    isPlaying,
    showResumePrompt,
  ]);

  // Save resume position for non-songs
  useEffect(() => {
    if (
      data?.category_id !== null &&
      isPlayerReady &&
      currentTime !== 0 &&
      !isVideoEndState
    ) {
      if (
        currentTime < duration - 30 &&
        currentTime > 10 &&
        data?.category_id !== "1"
      ) {
        localStorage.setItem(selectedEpisode.video_id, currentTime);
      } else if (currentTime > duration - 30 || currentTime < 10) {
        localStorage.removeItem(selectedEpisode.video_id);
      }
    }
  }, [
    data,
    isPlayerReady,
    currentTime,
    duration,
    isVideoEndState,
    selectedEpisode.video_id,
  ]);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const mm = m < 10 ? `0${m}` : m;
    const ss = s < 10 ? `0${s}` : s;
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
  };

  // Visual progress
  const preview = seekPreviewTime !== null ? seekPreviewTime : currentTime;
  const previewPercent = duration ? (preview / duration) * 100 : 0;

  return (
    <div
      className="progress-wrapper"
      style={{ display: "flex", alignItems: "center", padding: "0 10px" }}
    >
      <span
        className={`current-time fw-bold ${isSeeking ? "text-secondary" : ""}`}
        style={{
          minWidth: "50px",
          textAlign: "right",
          fontSize: "20px",
        }}
      >
        {formatTime(preview)}
      </span>

      <div
        className="progress-bar-container focusable"
        style={{
          flexGrow: 1,
          height: "8px",
          position: "relative",
          backgroundColor: "#333",
          borderRadius: "10px",
          overflow: "hidden",
          margin: "0 10px",
        }}
        onClick={(e) => {
          if (!playerRef.current || !isPlayerReady) return;
      
          const rect = e.currentTarget.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const percent = clickX / rect.width;
          const seekTime = percent * duration;
      
          playerRef.current.seekTo(seekTime, true);
          playerRef.current.playVideo();
          setSeekPreviewTime(null);
          dispatch(setCurrentWatch({ isSeeking: false }));
        }}
      >
        <div
          className={`progress-bar ${
            isProgressBarFocused ? "border border-dark" : ""
          }`}
          style={{
            width: `${previewPercent}%`,
            height: "100%",
            backgroundColor: isProgressBarFocused
              ? "var(--orange)"
              : "var(--red)",
            transition: "width 0.2s ease",
          }}
        />
      </div>

      <span
        className="total-duration fw-bold "
        style={{
          padding: "0 10px",
          minWidth: "50px",
          textAlign: "left",
          fontSize: "20px",
        }}
      >
        {formatTime(duration)}
      </span>
    </div>
  );
};

export default ProgressBar;
